import React, { useEffect, useMemo, useState } from 'react';
import { FaPause, FaPlay } from 'react-icons/fa';
import { mediaUrl, playVoice, stopVoice, voiceLabel, voiceUsable } from './docShared';

/** Round play / pause button for a voice preview. */
export function PlayVoice({ id, voice, label }) {
  const [state, setState] = useState(null);
  useEffect(() => () => { if (state === 'playing') stopVoice(); }, [state]);
  const busy = state === 'loading';
  return (
    <button type="button" className={`vp-play ${state === 'playing' ? 'is-on' : ''} ${state === 'error' ? 'is-err' : ''}`}
            disabled={!voice || busy}
            onClick={(e) => { e.stopPropagation(); playVoice(id, voice, setState); }}
            aria-label={state === 'playing' ? `Stop ${label}` : `Play ${label}`}
            title={state === 'error' ? 'No preview available' : `Hear ${label}`}>
      {busy ? <span className="vp-spin" /> : state === 'playing' ? <FaPause /> : <FaPlay />}
    </button>
  );
}

export function VoiceAvatar({ voice, name }) {
  if (voice?.avatar_image) return <img className="vp-avatar" src={voice.avatar_image} alt="" />;
  const initials = (name || '?').split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  return <span className="vp-avatar vp-avatar--txt" aria-hidden="true">{initials}</span>;
}

/** Searchable list of voices (Speechify and Inworld) with previews. */
export function VoicePicker({ voices, selected, onPick, onCancel }) {
  const [q, setQ] = useState('');
  const [lang, setLang] = useState('en');
  const [provider, setProvider] = useState('all');
  const hasInworld = useMemo(() => (voices || []).some((v) => v.provider === 'inworld'), [voices]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (voices || [])
      .filter((v) => provider === 'all' || (v.provider || 'speechify') === provider)
      .filter((v) => lang === 'all' || (v.locale || '').toLowerCase().startsWith(lang))
      .filter((v) => !s || `${v.display_name} ${v.gender} ${(v.tags || []).join(' ')}`.toLowerCase().includes(s))
      .sort((a, b) => (b.type === 'personal') - (a.type === 'personal')
        || (a.display_name || '').localeCompare(b.display_name || ''))
      .slice(0, 120);
  }, [voices, q, lang, provider]);

  if (!voices) return <div className="vp-picker vp-picker--loading">Loading voices…</div>;
  return (
    <div className="vp-picker">
      <div className="vp-picker__bar">
        <input className="docs-input" placeholder="Search voices by name, gender or style" value={q}
               onChange={(e) => setQ(e.target.value)} autoFocus />
        <select className="docs-input vp-lang" value={lang} onChange={(e) => setLang(e.target.value)}
                aria-label="Language">
          <option value="en">English</option>
          <option value="all">All languages</option>
        </select>
        {hasInworld && (
          <select className="docs-input vp-lang" value={provider} onChange={(e) => setProvider(e.target.value)}
                  aria-label="Voice provider">
            <option value="all">All voices</option>
            <option value="speechify">Speechify</option>
            <option value="inworld">Inworld</option>
          </select>
        )}
        {onCancel && <button type="button" className="docs-btn docs-btn--ghost" onClick={onCancel}>Close</button>}
      </div>
      <ul className="vp-list">
        {list.map((v) => (
          <li key={v.id} className={selected === v.id ? 'is-on' : ''}>
            <VoiceAvatar voice={v} name={v.display_name} />
            <span className="vp-list__name">
              <strong>{v.display_name}</strong>
              <span>{[v.provider === 'inworld' ? 'Inworld' : null,
                v.type === 'personal' ? 'Your cloned voice' : null,
                v.gender && v.gender !== 'notSpecified' ? v.gender.charAt(0).toUpperCase() + v.gender.slice(1) : null,
                v.locale].filter(Boolean).join(', ')}</span>
            </span>
            <PlayVoice id={`pick-${v.id}`} voice={v} label={v.display_name} />
            <button type="button" className={`docs-btn docs-btn--small ${selected === v.id ? 'docs-btn--primary' : ''}`}
                    onClick={() => onPick(v)}>
              {selected === v.id ? 'Chosen' : 'Choose'}
            </button>
          </li>
        ))}
        {list.length === 0 && <li className="vp-list__empty">No voices match. Try another search.</li>}
      </ul>
    </div>
  );
}

/** Which voice a channel will narrate documentaries with, and whether it can make word timings. */
export function channelVoiceInfo(ch, kit, list, engineVoices) {
  const own = ch?.voice || null;
  const ownName = own ? (own.provider === 'cartesia' && !own.name ? 'a Cartesia voice'
    : voiceLabel(own.id, list, own.name)) : null;
  const ownUsable = voiceUsable(own, list, engineVoices);
  const override = kit?.voice ? { id: kit.voice, name: kit.voice_name || voiceLabel(kit.voice, list) } : null;
  const effective = override || (ownUsable ? { id: own.id, name: ownName } : null);
  const objFor = (id) => (list || []).find((v) => v.id === id)
    || (/^[0-9a-f-]{36}$/i.test(id || '') ? { id } : null);
  return { own, ownName, ownUsable, override, effective, effectiveObj: effective ? objFor(effective.id) : null };
}

export function ChannelCard({ ch, kit, info, active, onPick, lookTitle }) {
  const logo = ch.logo_url ? mediaUrl(ch.logo_url) : null;
  const [broken, setBroken] = useState(false);
  const status = info.effective
    ? (info.override ? 'Documentary voice' : 'Channel voice')
    : (info.own ? 'Voice needs replacing' : 'No voice yet');
  return (
    <div role="radio" tabIndex={0} aria-checked={active} className={`dch-card ${active ? 'is-on' : ''}`}
         onClick={onPick}
         onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(); } }}>
      <span className="dch-card__head">
        {logo && !broken
          ? <img className="dch-card__logo" src={logo} alt="" onError={() => setBroken(true)} />
          : <span className="dch-card__logo dch-card__logo--txt">{(ch.name || '?').slice(0, 1).toUpperCase()}</span>}
        <span className="dch-card__title">
          <strong>{ch.name}</strong>
          <span>{ch.niche || 'No niche set'}</span>
        </span>
        <span className="dch-card__check" aria-hidden="true" />
      </span>
      <span className={`dch-card__voice ${info.effective ? '' : 'is-missing'}`}>
        <VoiceAvatar voice={info.effectiveObj} name={info.effective?.name || '?'} />
        <span className="dch-card__vname">
          <span className="dch-card__vlabel">{status}</span>
          <strong>{info.effective?.name || (info.own ? info.ownName : 'Choose a voice below')}</strong>
        </span>
        {info.effective && <PlayVoice id={`ch-${ch.id}`} voice={info.effectiveObj} label={info.effective.name} />}
      </span>
      <span className="dch-card__kit">
        {kit?.look ? `Brand kit: ${lookTitle || kit.look}` : 'No brand kit yet'}
        {kit?.accent && <span className="dch-card__dot" style={{ background: kit.accent }} />}
      </span>
    </div>
  );
}
